import React, { useState, useEffect } from 'react';
import { useParams, useNavigate, Link } from 'react-router-dom';
import { ArrowLeft, Save, AlertTriangle, Globe } from 'lucide-react';
import sitesApi from '../../services/sitesApi';
import ConfirmModal from '../../components/ConfirmModal';
import { useToast } from '../../context/ToastContext';
import { useSiteAuth } from '../../context/SiteAuthContext';

export default function SiteSettings() {
    const { id } = useParams();
    const navigate = useNavigate();
    const { siteUser } = useSiteAuth();
    const [site, setSite] = useState(null);
    const [loading, setLoading] = useState(true);
    const [saving, setSaving] = useState(false);
    const [showDeleteModal, setShowDeleteModal] = useState(false);
    const [formData, setFormData] = useState({
        name: '',
        slug: '',
        custom_domain: '',
        contact_email: '',
        logo_url: '',
        favicon_url: ''
    });
    const toast = useToast();
    const [uploadingLogo, setUploadingLogo] = useState(false);

    useEffect(() => {
        fetchSite();
    }, [id]);

    const fetchSite = async () => {
        try {
            const res = await sitesApi.getSite(id);
            const siteData = res.data.site || res.data;
            setSite(siteData);
            setFormData({
                name: siteData.name || '',
                slug: siteData.slug || '',
                custom_domain: siteData.custom_domain || '',
                contact_email: siteData.contact_email || '',
                logo_url: siteData.logo_url || '',
                favicon_url: siteData.favicon_url || ''
            });
        } catch (err) {
            console.error(err);
            toast.error('Failed to load settings');
        } finally {
            setLoading(false);
        }
    };

    const handleLogoUpload = async (e) => {
        const file = e.target.files[0];
        if (!file) return;

        setUploadingLogo(true);
        const data = new FormData();
        data.append('image', file);

        try {
            const res = await sitesApi.post('/api/sites/upload', data, {
                headers: { 'Content-Type': 'multipart/form-data' }
            });
            const imageUrl = res.data.secureUrl;
            setFormData(prev => ({
                ...prev,
                logo_url: imageUrl,
                favicon_url: imageUrl
            }));
            toast.success('Icon uploaded successfully!');
        } catch (err) {
            console.error('Logo upload error', err);
            toast.error(err.response?.data?.error || 'Failed to upload icon.');
        } finally {
            setUploadingLogo(false);
        }
    };

    const handleSave = async (e) => {
        e.preventDefault();
        setSaving(true);
        try {
            await sitesApi.updateSettings(id, formData);
            toast.success('Settings saved successfully!');
            navigate('/sites/dashboard');
        } catch (err) {
            console.error(err);
            toast.error(err.response?.data?.message || 'Failed to save settings.');
        } finally {
            setSaving(false);
        }
    };

    if (loading) {
        return <div className="min-h-screen bg-slate-950 flex items-center justify-center"><div className="animate-spin rounded-full h-10 w-10 border-t-2 border-b-2 border-primary-500"></div></div>;
    }

    return (
        <div className="min-h-screen bg-slate-950 text-slate-300">
            <div className="bg-slate-900 border-b border-slate-800 py-6">
                <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 flex items-center justify-between">
                    <div className="flex items-center gap-4">
                        <button onClick={() => navigate('/sites/dashboard')} className="text-slate-400 hover:text-white transition-colors">
                            <ArrowLeft size={20} />
                        </button>
                        <h1 className="text-2xl font-bold text-white">Site Settings</h1>
                    </div>
                </div>
            </div>

            <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
                <form onSubmit={handleSave} className="space-y-8">
                    {/* General Settings */}
                    <div className="bg-slate-900 border border-slate-800 rounded-2xl p-6 md:p-8">
                        <h2 className="text-xl font-bold text-white mb-6">General Information</h2>
                        
                        <div className="space-y-6">
                            <div>
                                <label className="block text-sm font-medium text-slate-300 mb-2">Site Name</label>
                                <input
                                    type="text"
                                    className="w-full bg-slate-950 border border-slate-700 rounded-xl px-4 py-2.5 text-white focus:outline-none focus:border-primary-500 focus:ring-1 focus:ring-primary-500"
                                    value={formData.name}
                                    onChange={(e) => setFormData({...formData, name: e.target.value})}
                                    required
                                />
                            </div>

                            <div>
                                <label className="block text-sm font-medium text-slate-300 mb-2">Subdomain</label>
                                <div className="flex rounded-xl overflow-hidden border border-slate-700 focus-within:border-primary-500 focus-within:ring-1 focus-within:ring-primary-500">
                                    <input
                                        type="text"
                                        className="flex-1 bg-slate-950 px-4 py-2.5 text-white focus:outline-none"
                                        value={formData.slug}
                                        onChange={(e) => setFormData({...formData, slug: e.target.value.toLowerCase().replace(/[^a-z0-9-]/g, '')})}
                                        required
                                    />
                                    <div className="bg-slate-800 px-4 py-2.5 text-slate-400 border-l border-slate-700 flex items-center">
                                        .aaharamcloud.com
                                    </div>
                                </div>
                                <p className="mt-2 text-sm text-slate-500">Your site will be accessible at: <a href={`http://${formData.slug}.aaharamcloud.com`} target="_blank" rel="noreferrer" className="text-primary-500 hover:underline">{formData.slug}.aaharamcloud.com</a></p>
                            </div>

                            <div>
                                <label className="block text-sm font-medium text-slate-300 mb-2">Website Icon / Logo</label>
                                <div className="flex items-center gap-6 p-4 bg-slate-950/40 rounded-xl border border-slate-800">
                                    <div className="w-16 h-16 bg-slate-900 border border-slate-700 rounded-xl flex items-center justify-center overflow-hidden shrink-0">
                                        {formData.logo_url ? (
                                            <img src={formData.logo_url} alt="Logo" className="w-full h-full object-contain" />
                                        ) : (
                                            <span className="text-xs text-slate-500">No logo</span>
                                        )}
                                    </div>
                                    <div className="flex-grow">
                                        <input
                                            type="file"
                                            id="logo-upload"
                                            accept="image/*"
                                            className="hidden"
                                            onChange={handleLogoUpload}
                                            disabled={uploadingLogo}
                                        />
                                        <label
                                            htmlFor="logo-upload"
                                            className={`inline-flex items-center gap-2 bg-slate-800 hover:bg-slate-700 text-white px-4 py-2 rounded-lg text-sm font-medium cursor-pointer transition-colors ${uploadingLogo ? 'opacity-50 cursor-not-allowed' : ''}`}
                                        >
                                            {uploadingLogo ? 'Uploading...' : 'Upload Icon'}
                                        </label>
                                        <p className="mt-2 text-xs text-slate-500 font-medium">This image will be used as both your website header logo and browser favicon.</p>
                                    </div>
                                </div>
                            </div>

                            <div>
                                <label className="block text-sm font-medium text-slate-300 mb-2">Contact Inquiry Email</label>
                                <input
                                    type="email"
                                    placeholder="Leave blank to use your account email"
                                    className="w-full bg-slate-950 border border-slate-700 rounded-xl px-4 py-2.5 text-white focus:outline-none focus:border-primary-500 focus:ring-1 focus:ring-primary-500"
                                    value={formData.contact_email}
                                    onChange={(e) => setFormData({...formData, contact_email: e.target.value})}
                                />
                                <p className="mt-2 text-xs text-slate-500">Form inquiries submitted on your website will be emailed to this address. If left blank, emails default to your account login address.</p>
                            </div>
                        </div>
                    </div>

                    {/* Domain Settings */}
                    <div className="bg-slate-900 border border-slate-800 rounded-2xl p-6 md:p-8 relative overflow-hidden">
                        <div className="absolute top-0 right-0 p-4">
                            {siteUser?.plan_name === 'Free' && (
                                <span className="bg-amber-500/10 text-amber-500 border border-amber-500/20 px-3 py-1 rounded-full text-xs font-bold">PAID FEATURE</span>
                            )}
                            {siteUser?.plan_name === 'Basic' && (
                                <span className="bg-primary-500/10 text-primary-400 border border-primary-500/20 px-3 py-1 rounded-full text-xs font-bold">BASIC (1 Domain)</span>
                            )}
                            {siteUser?.plan_name === 'Pro' && (
                                <span className="bg-emerald-500/10 text-emerald-400 border border-emerald-500/20 px-3 py-1 rounded-full text-xs font-bold font-mono">PRO (Unlimited)</span>
                            )}
                        </div>
                        <h2 className="text-xl font-bold text-white mb-2 flex items-center gap-2"><Globe size={20}/> Custom Domain</h2>
                        <p className="text-slate-400 text-sm mb-6">Connect your own domain (e.g., yourrestaurant.com) to this site.</p>
                        
                        <div>
                            <label className="block text-sm font-medium text-slate-300 mb-2">Domain Name</label>
                            <input
                                type="text"
                                disabled={siteUser?.plan_name === 'Free'}
                                className="w-full bg-slate-950 border border-slate-700 rounded-xl px-4 py-2.5 text-white focus:outline-none focus:border-primary-500 focus:ring-1 focus:ring-primary-500 disabled:opacity-50 disabled:cursor-not-allowed"
                                placeholder={siteUser?.plan_name !== 'Free' ? "www.yourrestaurant.com" : "Connect custom domain (Paid feature)"}
                                value={formData.custom_domain}
                                onChange={(e) => setFormData({...formData, custom_domain: e.target.value})}
                            />
                            {siteUser?.plan_name !== 'Free' ? (
                                <>
                                    {formData.custom_domain && (
                                        <div className="mt-6 bg-slate-950 border border-slate-800 rounded-2xl p-6 text-sm text-slate-300">
                                            <p className="mb-4 font-bold text-white text-base flex items-center gap-2">
                                                <Globe className="text-primary-500 w-5 h-5 animate-pulse" />
                                                DNS Configuration Required
                                            </p>
                                            <p className="mb-4 text-xs text-slate-400 leading-relaxed">
                                                To point your domain to our servers, log in to your domain registrar (GoDaddy, Namecheap, etc.) and add the following records to your domain's DNS zone file:
                                            </p>
                                            
                                            <div className="space-y-4">
                                                <div className="bg-slate-900 border border-slate-800 rounded-xl p-4 space-y-2">
                                                    <span className="text-xs font-black text-indigo-400 uppercase tracking-wider block">1. Root Domain Configuration ({formData.custom_domain.replace(/^www\./i, '')})</span>
                                                    <div className="grid grid-cols-3 gap-2 font-mono text-xs pt-1 border-t border-slate-800 text-slate-400">
                                                        <div><span className="text-slate-500 block text-[10px] uppercase font-sans font-bold font-semibold">Type</span>A</div>
                                                        <div><span className="text-slate-500 block text-[10px] uppercase font-sans font-bold font-semibold">Name</span>@</div>
                                                        <div><span className="text-slate-500 block text-[10px] uppercase font-sans font-bold font-semibold">Value</span>216.198.79.1</div>
                                                    </div>
                                                </div>

                                                <div className="bg-slate-900 border border-slate-800 rounded-xl p-4 space-y-2">
                                                    <span className="text-xs font-black text-indigo-400 uppercase tracking-wider block">2. WWW Subdomain Configuration (www.{formData.custom_domain.replace(/^www\./i, '')})</span>
                                                    <div className="grid grid-cols-3 gap-2 font-mono text-xs pt-1 border-t border-slate-800 text-slate-400">
                                                        <div><span className="text-slate-500 block text-[10px] uppercase font-sans font-bold font-semibold">Type</span>CNAME</div>
                                                        <div><span className="text-slate-500 block text-[10px] uppercase font-sans font-bold font-semibold">Name</span>www</div>
                                                        <div><span className="text-slate-500 block text-[10px] uppercase font-sans font-bold font-semibold">Value</span>cname.vercel-dns.com</div>
                                                    </div>
                                                </div>
                                            </div>
                                            
                                            <p className="mt-4 text-[11px] text-amber-500/80 leading-relaxed font-sans">
                                                * Note: DNS changes can take anywhere from a few minutes to 24 hours to fully propagate globally. Vercel will automatically verify the connection and provision SSL encryption once active.
                                            </p>
                                        </div>
                                    )}
                                    {siteUser?.plan_name === 'Basic' && (
                                        <p className="mt-3 text-xs text-slate-500">Your Basic plan supports connecting 1 custom domain across your sites. Upgrade to Pro for unlimited custom domains.</p>
                                    )}
                                </>
                            ) : (
                                <div className="mt-4 bg-slate-950/60 border border-slate-800 rounded-xl p-4 text-sm text-slate-500 flex items-center justify-between">
                                    <span>Custom domains are not supported on the Free plan.</span>
                                    <button
                                        type="button"
                                        onClick={() => navigate('/sites/subscription')}
                                        className="bg-amber-600 hover:bg-amber-700 text-white px-3 py-1.5 rounded-lg text-xs font-bold transition-colors"
                                    >
                                        Upgrade Plan
                                    </button>
                                </div>
                            )}
                        </div>
                    </div>

                    <div className="flex justify-end gap-4 pt-4">
                        <button type="button" onClick={() => navigate('/sites/dashboard')} className="px-6 py-2.5 rounded-xl text-slate-300 hover:bg-slate-800 transition-colors">
                            Cancel
                        </button>
                        <button type="submit" disabled={saving} className="flex items-center gap-2 bg-primary-600 hover:bg-primary-700 text-white px-6 py-2.5 rounded-xl font-medium transition-colors disabled:opacity-50">
                            <Save size={18} />
                            {saving ? 'Saving...' : 'Save Settings'}
                        </button>
                    </div>
                </form>

                {/* Danger Zone */}
                <div className="mt-12 bg-red-950/20 border border-red-900/50 rounded-2xl p-6 md:p-8">
                    <h2 className="text-xl font-bold text-red-500 mb-2 flex items-center gap-2"><AlertTriangle size={20}/> Danger Zone</h2>
                    <p className="text-red-400/80 text-sm mb-6">Once you delete a site, there is no going back. Please be certain.</p>
                    <button 
                        onClick={() => setShowDeleteModal(true)}
                        className="bg-red-500/10 hover:bg-red-500/20 text-red-500 border border-red-500/30 px-6 py-2.5 rounded-xl font-medium transition-colors"
                    >
                        Delete Site
                    </button>
                </div>
            </div>

            {/* Deletion Confirm Modal */}
            <ConfirmModal
                isOpen={showDeleteModal}
                title="Delete Site"
                message="Are you absolutely sure you want to delete this site? This action is permanent and cannot be undone."
                confirmText="Delete"
                cancelText="Cancel"
                onConfirm={async () => {
                    setShowDeleteModal(false);
                    try {
                        await sitesApi.deleteSite(id);
                        toast.success('Site deleted successfully!');
                        navigate('/sites/dashboard');
                    } catch (err) {
                        toast.error('Failed to delete site.');
                    }
                }}
                onCancel={() => setShowDeleteModal(false)}
                variant="danger"
            />
        </div>
    );
}
